import { useState, useCallback, useEffect } from "react"
import { debounce } from "ts-debounce"
import backend from "~/backend"
import useDidUpdateEffect from "~/hooks/useDidUpdateEffect"
import { generateId } from "~/utils"

type ColorKey =
  | "sapphire"
  | "success"
  | "warning"
  | "plum"
  | "slate"
  | "blue"
  | "green"
  | "amber"
  | "purple"

export type StickyNote = {
  id: string
  label: string
  body: string
  color: ColorKey
}

type Props = {
  notes: StickyNote[] | null
}

const COLOR_SCHEMES: Record<
  ColorKey,
  {
    pill: string
    border: string
    background: string
    focus: string
    accent: string
  }
> = {
  sapphire: {
    pill: "bg-sapphire/20 text-sapphire",
    border: "border-sapphire",
    background: "bg-sapphire/10",
    focus: "focus:ring-sapphire focus:border-sapphire",
    accent: "text-sapphire"
  },
  success: {
    pill: "bg-success/20 text-success",
    border: "border-success",
    background: "bg-success/10",
    focus: "focus:ring-success focus:border-success",
    accent: "text-success"
  },
  warning: {
    pill: "bg-warning/20 text-warning",
    border: "border-warning",
    background: "bg-warning/10",
    focus: "focus:ring-warning focus:border-warning",
    accent: "text-warning"
  },
  plum: {
    pill: "bg-plum/20 text-plum",
    border: "border-plum",
    background: "bg-plum/10",
    focus: "focus:ring-plum focus:border-plum",
    accent: "text-plum"
  },
  slate: {
    pill: "bg-slate/20 text-slate",
    border: "border-slate",
    background: "bg-slate/10",
    focus: "focus:ring-slate focus:border-slate",
    accent: "text-slate"
  },
  // Legacy color mappings for backward compatibility
  blue: {
    pill: "bg-sapphire/20 text-sapphire",
    border: "border-sapphire",
    background: "bg-sapphire/10",
    focus: "focus:ring-sapphire focus:border-sapphire",
    accent: "text-sapphire"
  },
  green: {
    pill: "bg-success/20 text-success",
    border: "border-success",
    background: "bg-success/10",
    focus: "focus:ring-success focus:border-success",
    accent: "text-success"
  },
  amber: {
    pill: "bg-warning/20 text-warning",
    border: "border-warning",
    background: "bg-warning/10",
    focus: "focus:ring-warning focus:border-warning",
    accent: "text-warning"
  },
  purple: {
    pill: "bg-plum/20 text-plum",
    border: "border-plum",
    background: "bg-plum/10",
    focus: "focus:ring-plum focus:border-plum",
    accent: "text-plum"
  }
}

const COLOR_OPTIONS: { value: ColorKey; label: string }[] = [
  { value: "sapphire", label: "Sapphire" },
  { value: "success", label: "Green" },
  { value: "warning", label: "Yellow" },
  { value: "plum", label: "Plum" },
  { value: "slate", label: "Slate" }
]

const DEFAULT_COLOR: ColorKey = "sapphire"

const StickyNotes = ({ notes: initialNotes }: Props) => {
  const [notes, setNotes] = useState<StickyNote[]>(() =>
    initialNotes && initialNotes.length > 0 ? initialNotes : []
  )
  const [draft, setDraft] = useState({
    label: "",
    body: "",
    color: DEFAULT_COLOR as ColorKey
  })
  const [showForm, setShowForm] = useState(false)

  const persist = useCallback(
    debounce((payload: StickyNote[]) => {
      backend.updateStickyNotes(payload)
    }, 300),
    []
  )

  useEffect(() => {
    setNotes(initialNotes && initialNotes.length > 0 ? initialNotes : [])
  }, [initialNotes])

  useDidUpdateEffect(() => {
    persist(notes)
  }, [notes])

  const removeNote = (id: string) => {
    setNotes((previous) => previous.filter((note) => note.id !== id))
  }

  const handleAdd = () => {
    const label = draft.label.trim()
    const body = draft.body.trim()

    if (!label && !body) return

    const newNote: StickyNote = {
      id: generateId(),
      label,
      body,
      color: draft.color
    }

    setNotes((previous) => [...previous, newNote])
    setDraft({ label: "", body: "", color: DEFAULT_COLOR })
    setShowForm(false)
  }

  const handleDraftChange = (
    field: "label" | "body" | "color",
    value: string
  ) => {
    setDraft((previous) => ({
      ...previous,
      [field]: field === "color" ? (value as ColorKey) : value
    }))
  }

  const handleCancel = () => {
    setDraft({ label: "", body: "", color: DEFAULT_COLOR })
    setShowForm(false)
  }

  const renderNote = (note: StickyNote) => {
    const scheme = COLOR_SCHEMES[note.color] || COLOR_SCHEMES[DEFAULT_COLOR]
    const hasBody = note.body && note.body.trim().length > 0
    const labelText =
      note.label && note.label.trim().length > 0 ? note.label : "Note"

    return (
      <div key={note.id} className="group/note flex items-center gap-3">
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide ${scheme.pill}`}
        >
          {labelText}
        </span>
        <span className="flex-1 text-sm leading-tight text-fg">
          {hasBody ? (
            note.body
          ) : (
            <span className="italic text-fg-muted">No details added</span>
          )}
        </span>
        <button
          type="button"
          className="group-hover/note:opacity-100 text-danger opacity-0 transition hover:text-danger-hover"
          title="Delete note"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            removeNote(note.id)
          }}
        >
          <i className="fa-regular fa-trash-can text-xs" />
        </button>
      </div>
    )
  }

  return (
    <div className="group mt-4 space-y-1 border-l-4 border-slate px-4">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="text-lg font-medium">Sticky notes</div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="text-lg text-slate opacity-0 transition hover:font-medium focus:opacity-100 group-hover:opacity-100"
          title="Add sticky note"
        >
          +
        </button>
      </div>

      {notes.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate bg-slate/10 px-4 py-3 text-sm text-fg-muted">
          Save frequently used snippets like Wi-Fi details, addresses, or
          meeting links.
        </div>
      )}

      {notes.map(renderNote)}

      {showForm && (
        <div className="space-y-3 rounded-lg border border-dashed border-slate bg-slate/10 p-4">
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={draft.label}
              onChange={(event) =>
                handleDraftChange("label", event.target.value)
              }
              placeholder="Label (e.g. Wi-Fi)"
              className="min-w-[120px] grow rounded-full border border-transparent bg-white/80 px-3 py-2 text-xs font-medium uppercase tracking-wide text-fg focus:border-slate focus:outline-none focus:ring-2 focus:ring-slate"
            />

            <select
              value={draft.color}
              onChange={(event) =>
                handleDraftChange("color", event.target.value)
              }
              className="rounded-md border border-border bg-white p-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-slate"
            >
              {COLOR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <textarea
            value={draft.body}
            onChange={(event) => handleDraftChange("body", event.target.value)}
            placeholder="Add note details"
            className="w-full rounded-md border border-transparent bg-white/80 px-3 py-2 text-sm leading-relaxed focus:border-slate focus:outline-none focus:ring-2 focus:ring-slate"
            rows={3}
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center px-3 py-2 text-sm font-semibold text-fg-muted hover:text-fg"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!draft.label.trim() && !draft.body.trim()}
              className="inline-flex items-center rounded-md bg-slate px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate/80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save note
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default StickyNotes
